/**
 * agentTree — turn the reducer's flat run-level roster (``agentRoster``) into a nested tree for
 * the room's hierarchy panel, plus a flattened pre-order walk carrying the guide-line metadata a
 * renderer needs to draw ├─ / └─ / │ connectors.
 *
 * The roster is a flat array of {agentId, parentAgentId, level, ...} rows accreted from AppSync
 * events (see agentRoomReducer). This module is pure + defensive so it can be unit-tested without
 * React: bad/cyclic/orphan parents degrade to roots rather than throwing.
 */

/**
 * Merge the STABLE seeded roster (full topology from the manifest, all ``queued``) with the LIVE
 * reducer roster (status/text/reasoning/builder-stream accreted from AppSync). Keyed by agentId.
 *
 * This is what makes the panel STABLE: the seeded rows define the row SET + ORDER up front (so the
 * list never jumps as distributed agents report in); live rows only overlay mutable fields
 * (status, text, usage, reasoning, builderLines, builderCompletion, runId) onto the matching row.
 *
 * ``selected`` (did the Lead engage this agent for THIS run?) is derived: an agent that produced
 * ANY live signal (status advanced past ``queued``) was selected; one still ``queued`` was NOT
 * selected (the Lead routed around it). Non-selected rows render grayed-out; clicking one shows the
 * "not required" note + the Lead's routing reasoning. When ``runSettled`` is true (run finished),
 * a still-``queued`` row is definitively ``not-selected``; while the run is live it's just pending.
 *
 * A live row with no seeded match (topology drift / dynamic node) is appended (never dropped) so we
 * never lose a real agent — falls back to the old event-accreted behavior for that row.
 *
 * @param {Array<Object>} seeded  from manifestToRoster (stable topology)
 * @param {Array<Object>} live    reducer ``agentRoster``
 * @param {{runSettled?: boolean, stageDepthCap?: number,
 *          disabledByAgentId?: Object<string,'branch'|'self'>|null}} [opts]
 * @returns {Array<Object>} merged rows (seeded order preserved; drift appended), each with
 *   ``selected: boolean`` and ``notSelected: boolean`` (only true once the run has settled).
 */
export function mergeRoster(seeded, live, opts = {}) {
  const runSettled = !!opts.runSettled;
  // Manual stage cap (frozen release setting): stage-depth (row.level, 1-based) > cap → the agent
  // is part of the org but DISABLED this release (the user limited run depth). 0/absent → no cap.
  // Distinct from ``notSelected`` (a per-RUN Lead routing skip): ``cappedOut`` is a deliberate
  // CONFIG state the whole release carries, so the row reads "disabled this release", not "not
  // required for this task".
  const stageCap = Math.max(0, Number.parseInt(opts.stageDepthCap, 10) || 0);
  // ROOM-SCOPES Phase 1 — per-room agent disables ({agentId → 'branch'|'self'}). A ROOM-level
  // switch, distinct from both ``cappedOut`` (a release-wide depth cap) and ``notSelected`` (a
  // per-run Lead skip): the room OWNER parked this agent for THIS room only. ``'branch'`` also
  // turns off every DESCENDANT — a row inherits the branch disable when any nodePath segment
  // (its ancestry, e.g. 'root/A/B') matches a branch-disabled agentId.
  const disables = (opts.disabledByAgentId && typeof opts.disabledByAgentId === 'object')
    ? opts.disabledByAgentId : null;
  // RESULT REUSE (RESULT-REUSE.md) — {agentId → {capturedMilestone, capturedRunId}}. A reused agent
  // is bypassed this run (its prior output is fed instead), so the roster must read "reused from last
  // run", NOT the misleading "standby" of a never-engaged agent (AGENTIC-RUN-QUALITY honest-UI).
  const reuses = (opts.reuseByAgentId && typeof opts.reuseByAgentId === 'object')
    ? opts.reuseByAgentId : null;
  const reuseFor = (row) => {
    if (!reuses) return null;
    const m = reuses[String(row.agentId)];
    if (!m) return null;
    return m.capturedRunId ? String(m.capturedRunId) : true;   // runId for the badge, else just "on"
  };
  const branchDisabled = new Set(
    disables ? Object.keys(disables).filter((id) => disables[id] === 'branch') : [],
  );
  /** Effective room-disable for a row: its OWN mode first, else 'branch' when a branch-disabled
   *  ancestor appears in its nodePath (segments are agentIds), else the parent link as a fallback
   *  for drift rows that carry ``parentAgentId`` but no nodePath. Null when not disabled. */
  const roomDisableFor = (row) => {
    if (!disables) return null;
    const own = disables[String(row.agentId)];
    if (own === 'branch' || own === 'self') return own;
    if (!branchDisabled.size) return null;
    const path = typeof row.nodePath === 'string' ? row.nodePath : '';
    if (path) {
      const segs = path.split('/');
      // Ancestors only — the row's own segment was already handled by ``own`` above.
      for (let i = 0; i < segs.length - 1; i += 1) {
        if (branchDisabled.has(segs[i])) return 'branch';
      }
    } else if (row.parentAgentId && branchDisabled.has(String(row.parentAgentId))) {
      return 'branch';
    }
    return null;
  };
  // TASK-BOUND focus (project rooms): the exact agents the current task assigned (leadAgent +
  // involvedAgents) + their ANCESTORS (a group lead must remain visible to reach its involved
  // child) + their DESCENDANTS' path context. Rows OUTSIDE this set are flagged ``notInTask`` so
  // the UI can dim/hide agents the task doesn't need — the "show only relevant agents" ask. Null/
  // empty → not a task-bound run → the flag is never set (every row stays in-task). ``involvedSet``
  // is a Set of agentIds; ``involvedPaths`` a Set of nodePath prefixes to keep ancestors in.
  const involvedRaw = opts.involvedAgents;
  const involvedSet = Array.isArray(involvedRaw) && involvedRaw.length
    ? new Set(involvedRaw.map((a) => String(a)))
    : null;
  /** A row is IN the task when: it's an involved agent itself, OR an ANCESTOR of one (an involved
   *  agent's nodePath contains this row's agentId), OR a DESCENDANT of one (this row's nodePath
   *  contains an involved agentId) — so the whole relevant sub-tree (group lead → involved leaves)
   *  stays lit and everything else dims. Engine-stage rows follow their node's nodePath. */
  const inTaskFor = (row, involvedNodePaths) => {
    if (!involvedSet) return true;                 // not a task-bound run → everyone in
    const id = String(row.agentId);
    if (involvedSet.has(id)) return true;
    const path = typeof row.nodePath === 'string' ? row.nodePath : '';
    if (path) {
      const segs = path.split('/');
      // Ancestor: an involved agent sits below this row (its id appears in an involved path).
      for (const p of involvedNodePaths) {
        if (p === path || p.startsWith(`${path}/`)) return true;
      }
      // Descendant: this row sits below an involved agent (an involved id is on its path).
      for (const seg of segs) {
        if (involvedSet.has(seg)) return true;
      }
    }
    return false;
  };
  const seededArr = Array.isArray(seeded) ? seeded.filter((r) => r && r.agentId) : [];
  const liveArr = Array.isArray(live) ? live.filter((r) => r && r.agentId) : [];
  // Precompute the nodePaths of the involved agents (from the seeded topology) so ancestor rows
  // can be kept in-task. Built once over the seeded roster (the stable full topology).
  const involvedNodePaths = involvedSet
    ? seededArr.filter((r) => involvedSet.has(String(r.agentId)))
      .map((r) => (typeof r.nodePath === 'string' ? r.nodePath : ''))
      .filter(Boolean)
    : [];
  const liveById = new Map(liveArr.map((r) => [String(r.agentId), r]));
  const usedLive = new Set();

  const decorate = (row) => {
    const advanced = row.status && row.status !== 'queued';
    const lvl = Number(row.level);
    const cappedOut = stageCap > 0 && Number.isFinite(lvl) && lvl > stageCap;
    const disabledByRoom = roomDisableFor(row);
    // ``notInTask`` (task-bound project run): this row is NOT part of the current task's relevant
    // sub-tree → dim it. Distinct from ``notSelected`` (a per-run Lead skip, asserted only after
    // the run settles): notInTask is known UP FRONT from the plan, so the UI can focus on the
    // relevant agents the moment the task is chosen — before any event streams.
    const notInTask = involvedSet ? !inTaskFor(row, involvedNodePaths) : false;
    // Engine-stage rows (Lead/Reducer lines) are orchestration work, not routable agents — they're
    // never "not required" (they always run when their node runs) and never capped-out as an agent.
    // They DO inherit a branch disable via nodePath (a parked branch's Lead/Summarizer never run).
    const reusedFromRun = reuseFor(row);
    if (row.isEngineStage) {
      return { ...row, selected: !!advanced, notSelected: false, cappedOut: false, disabledByRoom, notInTask, reusedFromRun };
    }
    return {
      ...row,
      selected: !!advanced,
      // "Not required" is only asserted once the run is over — mid-run a queued agent may still run.
      notSelected: runSettled && !advanced,
      cappedOut,
      disabledByRoom,
      notInTask,
      reusedFromRun,
    };
  };

  const merged = seededArr.map((s) => {
    const lv = liveById.get(String(s.agentId));
    if (!lv) return decorate(s);
    usedLive.add(String(s.agentId));
    // Live overlays mutable fields onto the stable seeded row; seeded keeps identity/order/topology.
    return decorate({
      ...s,
      ...lv,
      // Prefer the seeded topology (stable) for tree placement; live may lack level/parent early.
      level: s.level ?? lv.level,
      parentAgentId: s.parentAgentId ?? lv.parentAgentId,
      nodePath: s.nodePath || lv.nodePath,
      label: lv.label || s.label,
    });
  });

  // Drift: any live agent not in the seeded topology → append (never lose a real agent).
  for (const lv of liveArr) {
    if (!usedLive.has(String(lv.agentId))) merged.push(decorate(lv));
  }
  return merged;
}

/**
 * DYNAMIC NETWORK VIEW (DYNAMIC-NETWORK-VIEW): filter a merged roster down to what's ENGAGED this
 * run — so a 16-agent network renders only the ~3 the Lead actually used, not the whole static org
 * chart. Rendered incrementally: as AppSync events flip a row's ``status`` past ``queued``, it (and
 * its ancestor chain) appears.
 *
 * Keep-set (a connected sub-tree, never orphans):
 *   • ENGAGED rows — ``status`` advanced past ``queued`` (running/awaiting/done/failed/blocked).
 *     A FAILED agent stays visible (a failure is engagement you want to see).
 *   • Their ANCESTORS — every node whose ``agentId`` is a segment of an engaged row's ``nodePath``
 *     (fallback: the ``parentAgentId`` chain for drift rows without a nodePath), rendered FULL (not
 *     dimmed) so the tree reads as a connected path.
 *   • The ROOT COHORT (``level <= 1``) — the entry leads (incl. the Network Lead) ALWAYS show, so an
 *     un-started run still renders "Network Lead" (the seed) rather than an empty block.
 *   • Engine-stage rows (Lead/Reducer) + the Summary of a VISIBLE node — they follow their node.
 *
 * Pure + defensive. Returns a new array in the input's order (stable). ``rows`` is a merged roster
 * (from ``mergeRoster``); returns the same row objects, filtered.
 *
 * @param {Array<Object>} rows  merged roster
 * @returns {Array<Object>}     the engaged sub-tree (subset of ``rows``, order preserved)
 */
export function filterEngagedSubtree(rows) {
  const all = Array.isArray(rows) ? rows.filter((r) => r && r.agentId) : [];
  if (all.length === 0) return [];
  const engaged = (r) => {
    const s = String(r.status || '').trim().toLowerCase();
    return s && s !== 'queued';   // running/awaiting/done/failed/blocked = engaged (failed stays)
  };
  const byId = new Map(all.map((r) => [String(r.agentId), r]));
  const keep = new Set();

  // The Summary row is the deliverable — always keep it if present (its own block renders it).
  const summary = all.find((r) => String(r.agentId || '').trim().toLowerCase() === 'summary');

  const keepAncestors = (row) => {
    // Prefer nodePath segments (authoritative topology); fall back to the parentAgentId chain.
    const path = typeof row.nodePath === 'string' ? row.nodePath : '';
    const segs = path.split('/').filter((seg) => seg && seg !== 'root');
    if (segs.length) {
      for (const seg of segs) if (byId.has(seg)) keep.add(seg);
      return;
    }
    let cur = row;
    const seen = new Set();
    while (cur && cur.parentAgentId && !seen.has(String(cur.agentId))) {
      seen.add(String(cur.agentId));
      const pid = String(cur.parentAgentId);
      if (!byId.has(pid) || keep.has(pid)) break;
      keep.add(pid);
      cur = byId.get(pid);
    }
  };

  for (const r of all) {
    const id = String(r.agentId);
    // Root cohort (entry leads incl. Network Lead) always visible — the seed.
    if (Number(r.level) <= 1 && !r.isEngineStage) keep.add(id);
    if (engaged(r)) { keep.add(id); keepAncestors(r); }
  }
  // Engine-stage rows (Group Lead / Summarizer): keep ONLY when the GROUP actually did work — i.e.
  // the stage itself ENGAGED — never merely because its owner node is a visible root/ancestor. A
  // group whose specialists never fired has a still-``queued`` Lead+Summarizer, which is the same
  // noise as the queued specialists under it, so it stays hidden too. Timing is fine: a Group Lead
  // runs (status advances) BEFORE dispatching its children, so it's already engaged by the time any
  // child is — it reveals incrementally with the group. EXCEPTION: the ROOT stages (Network Lead /
  // Network Summarizer) are the run's frame — always kept, like the root cohort. The Summary
  // deliverable is always kept.
  for (const r of all) {
    if (summary && r === summary) { keep.add(String(r.agentId)); continue; }
    if (!r.isEngineStage) continue;
    const path = typeof r.nodePath === 'string' ? r.nodePath : '';
    const segs = path.split('/').filter((seg) => seg && seg !== 'root');
    const isRootStage = segs.length === 0;   // owner is the root cohort → Network Lead/Summarizer
    if (isRootStage || engaged(r)) keep.add(String(r.agentId));
  }

  return all.filter((r) => keep.has(String(r.agentId)));
}

/**
 * Build a parent→children tree from a flat roster.
 *
 * A row is a ROOT when its ``parentAgentId`` is absent/blank OR points at an id that isn't in the
 * roster (an orphan — e.g. the parent's event hasn't arrived yet, or we're mid-run). Children keep
 * the roster's given order (which the reducer already sorts by level then first-seen).
 *
 * @param {Array<Object>} roster
 * @returns {Array<{row: Object, children: Array}>}  root nodes, each with nested ``children``
 */
export function buildAgentTree(roster) {
  const rows = Array.isArray(roster) ? roster.filter((r) => r && r.agentId) : [];
  const byId = new Map(rows.map((r) => [String(r.agentId), r]));

  // Raw parent→child adjacency (may contain a cycle if the wire ever sent A↔B). We build the tree
  // nodes DURING an acyclic DFS below so a bad cycle can never produce a self-referential
  // ``children`` structure (which would make flatten/render infinite-loop).
  const childIds = new Map(rows.map((r) => [String(r.agentId), []]));
  const rootIds = [];
  for (const r of rows) {
    const id = String(r.agentId);
    const pid = (typeof r.parentAgentId === 'string' && r.parentAgentId.trim())
      ? r.parentAgentId.trim() : '';
    // Root when no parent, parent missing from roster (orphan — parent event not yet arrived), or
    // a self-loop.
    if (!pid || pid === id || !byId.has(pid)) rootIds.push(id);
    else childIds.get(pid).push(id);
  }

  // NEST specialists UNDER their Group Lead (display transform, data untouched). A group's router
  // ("Group Lead", ``__lead__{nodePath}``) DISPATCHED its specialists, so the tree reads better with
  // them one layer deeper — under the Lead — rather than as its flat siblings:
  //     Fundamentals Lead              Fundamentals Lead
  //     ├─ Group Lead          →       ├─ Group Lead
  //     ├─ Valuation Spec              │    ├─ Valuation Spec
  //     ├─ Earnings Spec               │    └─ Earnings Spec
  //     └─ Summarizer                  └─ Summarizer
  // The Summarizer (reducer) STAYS a sibling of the Group Lead — it folds the group AFTER the
  // specialists, a phase-peer of the router, not one of the routed workers. Only moves real
  // specialists (non-stage rows); groups with no engine-lead row are untouched (non-generative).
  for (const [pid, kids] of childIds) {
    const leadId = kids.find((cid) => {
      const r = byId.get(cid);
      return r && r.isEngineStage && r.stageKind !== 'reducer';
    });
    if (!leadId) continue;
    const specialists = kids.filter((cid) => {
      if (cid === leadId) return false;
      const r = byId.get(cid);
      return !(r && r.isEngineStage);   // keep stage rows (Lead + Summarizer) at the group level
    });
    if (!specialists.length) continue;
    const specialistSet = new Set(specialists);
    childIds.set(pid, kids.filter((cid) => !specialistSet.has(cid)));  // Lead + Summarizer remain
    childIds.get(leadId).push(...specialists);                          // specialists → under Lead
    void pid;
  }

  // ROOT COHORT — mirror the SAME nesting at the top level. The root specialists (entry agents) are
  // ROOTS (no parent), so the group loop above never reaches them → "Network Lead", the specialists,
  // and "Network Summarizer" all render as flat siblings at depth 0. The "Network Lead"
  // (``__lead__root``) DISPATCHED those specialists exactly like a Group Lead, so nest them one level
  // UNDER it — the run then reads route → team → fold at the top just like every sub-group. The
  // "Network Summarizer" (reducer) STAYS a root sibling (it folds AFTER, a phase-peer of the lead).
  // Data untouched; only affects the ROOT level (deeper groups already nest correctly).
  const rootLeadId = rootIds.find((cid) => {
    const r = byId.get(cid);
    return r && r.isEngineStage && r.stageKind !== 'reducer';
  });
  if (rootLeadId) {
    const rootSpecialists = rootIds.filter((cid) => {
      if (cid === rootLeadId) return false;
      const r = byId.get(cid);
      // Keep stage rows (Network Lead + Network Summarizer) at root — and the async PLANNER: it is
      // NOT part of the state function's cohort (f.synth fires it fire-and-forget), so it must not
      // be swept under the Network Lead like a dispatched specialist. It reads as a root PHASE row.
      return !(r && (r.isEngineStage || r.isPlanner));
    });
    if (rootSpecialists.length) {
      const rootSet = new Set(rootSpecialists);
      for (let i = rootIds.length - 1; i >= 0; i -= 1) {
        if (rootSet.has(rootIds[i])) rootIds.splice(i, 1);   // pull specialists out of the root level
      }
      childIds.get(rootLeadId).push(...rootSpecialists);      // …and re-parent them under Network Lead
    }
  }

  // SEMANTIC sibling order (not event-timing): within a group the LEAD (router) reads FIRST, the
  // specialists in the middle, and the SUMMARIZER (reducer) LAST — that's the lifecycle the user
  // expects (route → work → fold). Event ``seq`` alone drifted the Lead down next to the Summarizer
  // when its row happened to be created after the specialist finals (live/replay ordering). So we
  // rank engine-stage rows explicitly: lead = top (-1), reducer = bottom (+1), everyone else keeps
  // their relative (seq) order in the middle (0). Stable sort preserves the specialists' order.
  const siblingRank = (id) => {
    const r = byId.get(id);
    // Async PLANNER reads LAST — one step below "Network Summarizer": the run's lifecycle is
    // route → work → fold → (non-blocking) plan update. It spins while the Summarizer streams.
    if (r && r.isPlanner) return 2;
    if (r && r.isEngineStage) return r.stageKind === 'reducer' ? 1 : -1;
    return 0;
  };
  for (const list of childIds.values()) {
    list.sort((a, b) => siblingRank(a) - siblingRank(b));   // Array.sort is stable → middle keeps seq
  }
  // Same rank at the ROOT level: the "Network Lead" (``__lead__root``) reads ABOVE the group-lead
  // nodes, and "Network Summarizer" (``__reducer__root``) BELOW them — so the whole run reads
  // route → groups → fold, top to bottom. rootIds are level-1 siblings just like a group's children.
  rootIds.sort((a, b) => siblingRank(a) - siblingRank(b));

  const visited = new Set();
  const attach = (id) => {
    visited.add(id);
    const node = { row: byId.get(id), children: [] };
    for (const cid of childIds.get(id)) {
      if (!visited.has(cid)) node.children.push(attach(cid));  // back-edge (cycle) → skip
    }
    return node;
  };

  const roots = rootIds.map(attach);
  // Any node still unvisited belongs to a cycle-only component (no acyclic path from a root) —
  // promote it to a root so it's still rendered rather than silently dropped.
  for (const r of rows) {
    const id = String(r.agentId);
    if (!visited.has(id)) roots.push(attach(id));
  }

  return roots;
}

/**
 * Pre-order flatten of the tree into render rows carrying guide-line metadata.
 *
 * Each emitted row: { row, depth, isLast, ancestorsLast }.
 *   - ``depth``          0-based nesting depth (roots = 0), for indentation.
 *   - ``isLast``         true when this node is its parent's LAST child → draws └─ (else ├─).
 *   - ``ancestorsLast``  boolean[] (one per ancestor level) — true where that ancestor was a last
 *                        child, so the renderer knows whether to draw a vertical │ guide or a gap.
 *
 * @param {Array<Object>} roster
 * @returns {Array<{row: Object, depth: number, isLast: boolean, ancestorsLast: boolean[]}>}
 */
export function flattenAgentTree(roster) {
  const roots = buildAgentTree(roster);
  const out = [];
  const walk = (node, depth, isLast, ancestorsLast) => {
    out.push({ row: node.row, depth, isLast, ancestorsLast });
    const kids = node.children;
    kids.forEach((child, i) => {
      walk(child, depth + 1, i === kids.length - 1, [...ancestorsLast, isLast]);
    });
  };
  roots.forEach((root, i) => walk(root, 0, i === roots.length - 1, []));
  return out;
}

/**
 * Clean display label for a synthetic engine-stage row from its ``agentId`` (``__lead__{path}`` /
 * ``__reducer__{path}``). Used as a FALLBACK by renderers when a row's ``label`` is missing or is the
 * raw id — history/reconnect can restore a stage row WITHOUT its label (the AGENT_REASONING finalize
 * that set ``label:"Network Lead"`` isn't re-applied on every rehydrate), which used to leak
 * ``__lead__root`` into the tree. Mirrors ``engineStagePlacement`` label logic; a non-stage id passes
 * through unchanged.
 *
 * @param {string} agentId
 * @returns {string}
 */
export function engineStageLabel(agentId) {
  const id = String(agentId || '');
  const m = id.match(/^__(lead|reducer)__(.*)$/);
  if (!m) return id;
  const isRoot = !m[2] || m[2] === 'root';
  if (m[1] === 'reducer') return isRoot ? 'Network Summarizer' : 'Summarizer';
  return isRoot ? 'Network Lead' : 'Group Lead';
}
